<template>
  <div class="image-attachment" @dragover.prevent @drop.prevent="dropImage">
    <!-- Hidden file inputs -->
    <input ref="file" type="file" :accept="accept" :multiple="maxCount > 1" :disabled="disabled || isCompressing" hidden @change="selectImage($event, 'gallery')" />
    <input ref="camera" type="file" :accept="accept" capture="environment" :disabled="disabled || isCompressing" hidden @change="selectImage($event, 'camera')" />
    
    <p v-if="fileError" role="alert" class="text-danger small">{{ fileError }}</p>

    <!-- Processing / Watermarking overlay indicator -->
    <div v-if="isCompressing" class="attachment-loading-card">
      <div class="spinner-border spinner-border-sm text-primary mb-2" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <span class="small font-weight-bold text-primary">{{ loadingMessage }}</span>
    </div>

    <!-- MULTI-FILE MODE (maxCount > 1) -->
    <div v-else-if="maxCount > 1 && multiItems.length > 0" class="attachment-multi-container">
      <div class="d-flex align-items-center justify-content-between mb-2">
        <span class="font-weight-bold text-dark small">
          {{ $t('sendReport.attachedPhotos') || text('รูปภาพที่แนบ', 'Attached Photos') }}
          <span class="badge badge-primary ml-1">{{ multiItems.length }}/{{ maxCount }}</span>
        </span>
      </div>

      <!-- Grid of Uploaded Photos -->
      <div class="multi-cards-grid">
        <div v-for="(item, idx) in multiItems" :key="idx" class="attachment-card multi-item-card">
          <div
            class="attachment-preview clickable-preview"
            role="button"
            tabindex="0"
            :title="$t('sendReport.viewFullPhoto') || text('คลิกเพื่อดูรูปภาพขนาดเต็ม', 'Click to view full photo')"
            :aria-label="($t('sendReport.viewFullPhoto') || text('ดูรูปภาพขนาดเต็ม', 'View full photo')) + ': ' + (item.name || (idx + 1))"
            @click="openPreview(idx)"
            @keydown.enter="openPreview(idx)"
            @keydown.space.prevent="openPreview(idx)"
          >
            <img :src="item.url" :alt="text('รูปภาพ', 'Photo') + ' ' + (idx + 1)" />
            <div class="preview-badge" :title="$t('sendReport.viewFullPhoto') || text('คลิกเพื่อดูรูปภาพขนาดเต็ม', 'Click to view full photo')">
              <CIcon name="cil-magnifying-glass" size="sm" />
            </div>
            <div class="preview-overlay">
              <CIcon name="cil-magnifying-glass" size="lg" class="preview-overlay-icon" />
              <span class="preview-overlay-text">{{ $t('sendReport.clickToView') || text('คลิกเพื่อดูรูปภาพ', 'Click to view photo') }}</span>
            </div>
          </div>
          <div v-if="hasTimestamp && item.displayTime" class="attachment-timestamp-ribbon py-1 px-2">
            <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 4px;">
              <span class="small text-dark font-weight-bold ml-auto" style="font-size: 11px;">
                {{ getItemTimestampDisplay(item) }}
              </span>
            </div>
          </div>
          <div class="attachment-footer py-2 px-2 d-flex align-items-center justify-content-between">
            <span
              class="attachment-name text-truncate clickable-name"
              style="font-size: 11px; cursor: pointer;"
              :title="item.name"
              @click="openPreview(idx)"
            >
              {{ item.name }}
            </span>
            <button
              type="button"
              class="btn btn-sm btn-delete-image p-1"
              style="line-height: 1;"
              :title="text('ลบรูปนี้', 'Remove')"
              :disabled="disabled"
              @click.stop="removeMultiItem(idx)"
            >
              <CIcon name="cil-trash" size="sm" />
            </button>
          </div>
        </div>
      </div>

      <!-- Add more button (shown when slots remain) -->
      <div v-if="multiItems.length < maxCount" class="attachment-add-more mt-2">
        <button
          type="button"
          class="btn-add-more w-100"
          :disabled="disabled"
          @click="$refs.file.click()"
        >
          <CIcon name="cil-camera" size="sm" class="mr-1" />
          <span>{{ $t('sendReport.takeOrAddPhoto') || text('ถ่ายรูป/เพิ่มรูป', 'Take/Add photo') }}</span>
        </button>
      </div>
    </div>

    <!-- SINGLE FILE MODE: Uploaded Image Preview Card -->
    <div v-else-if="maxCount <= 1 && preview" class="attachment-card">
      <div
        class="attachment-preview"
        :class="{ 'clickable-preview': !previewError }"
        :role="!previewError ? 'button' : undefined"
        :tabindex="!previewError ? '0' : undefined"
        :title="!previewError ? ($t('sendReport.viewFullPhoto') || text('คลิกเพื่อดูรูปภาพขนาดเต็ม', 'Click to view full photo')) : undefined"
        :aria-label="!previewError ? ($t('sendReport.viewFullPhoto') || text('ดูรูปภาพขนาดเต็ม', 'View full photo')) : undefined"
        @click="!previewError && openPreview(0)"
        @keydown.enter="!previewError && openPreview(0)"
        @keydown.space.prevent="!previewError && openPreview(0)"
      >
        <div v-if="previewError" class="attachment-fallback text-center py-4 px-2">
          <CIcon name="cil-file" size="2xl" class="text-primary mb-2" />
          <div class="text-muted small font-weight-bold text-truncate max-w-100">{{ filename || 'Attached File' }}</div>
          <span class="badge badge-primary mt-1 text-uppercase">{{ fileExtension || 'FILE' }}</span>
        </div>
        <template v-else>
          <img :src="preview" :alt="$t('sendReport.imageLabel')" @error="previewError = true" />
          <div class="preview-badge" :title="$t('sendReport.viewFullPhoto') || text('คลิกเพื่อดูรูปภาพขนาดเต็ม', 'Click to view full photo')">
            <CIcon name="cil-magnifying-glass" size="sm" />
          </div>
          <div class="preview-overlay">
            <CIcon name="cil-magnifying-glass" size="lg" class="preview-overlay-icon" />
            <span class="preview-overlay-text">{{ $t('sendReport.clickToView') || text('คลิกเพื่อดูรูปภาพ', 'Click to view photo') }}</span>
          </div>
        </template>
      </div>

      <!-- Timestamp Ribbon (only shown when hasTimestamp is true, e.g. technician watermark) -->
      <div v-if="hasTimestamp && currentTimestampDisplay" class="attachment-timestamp-ribbon">
        <div class="d-flex align-items-center flex-wrap" style="gap: 6px;">
          <span class="small font-weight-bold text-dark">
            {{ currentTimestampDisplay }}
          </span>
        </div>
      </div>

      <div class="attachment-footer">
        <div class="attachment-info d-flex align-items-center flex-wrap mr-2" style="min-width: 0; max-width: 55%;">
          <span
            class="attachment-name clickable-name"
            :title="filename"
            :style="{ cursor: !previewError ? 'pointer' : 'default' }"
            @click="!previewError && openPreview(0)"
          >
            {{ filename || $t('sendReport.imageLabel') }}
          </span>
          <span v-if="formattedSize" class="badge badge-light border text-primary ml-1 font-weight-bold">{{ formattedSize }}</span>
        </div>
        <div class="attachment-actions">
          <!-- Change / Retake image -->
          <button type="button" class="btn btn-sm btn-choose-new" :disabled="disabled" @click="$refs.file.click()">
            <CIcon name="cil-camera" size="sm" class="mr-1" />{{ text('เปลี่ยนรูป/ถ่ายใหม่', 'Change/Retake') }}
          </button>
          <button v-if="removable" type="button" class="btn btn-sm btn-delete-image" :disabled="disabled" @click="handleRemove">
            <CIcon name="cil-trash" size="sm" class="mr-1" />{{ $t('sendReport.remove') || text('ลบ', 'Remove') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Choice Button (when empty): Single unified button -->
    <div v-else class="attachment-choices">
      <button
        type="button"
        class="attachment-pick btn-choose-photo w-100"
        :class="{ 'border-danger': invalid }"
        :disabled="disabled"
        @click="$refs.file.click()"
      >
        <CIcon name="cil-camera" size="lg" />
        <span>{{ $t('sendReport.takeOrAddPhoto') || text('ถ่ายรูป/เพิ่มรูป', 'Take/Add photo') }}</span>
      </button>
    </div>

    <!-- Image Preview Modal / Lightbox -->
    <div
      v-if="showPreviewModal"
      ref="previewDialog"
      class="image-preview-backdrop"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('sendReport.viewFullPhoto') || text('ดูรูปภาพขนาดเต็ม', 'View full-size photo')"
      tabindex="-1"
      @click.self="closePreview"
    >
      <div class="image-preview-dialog">
        <!-- Header -->
        <div class="image-preview-header">
          <div class="d-flex align-items-center" style="gap: 8px; min-width: 0;">
            <CIcon name="cil-magnifying-glass" class="text-primary flex-shrink-0" />
            <span class="preview-title text-truncate font-weight-bold" :title="activePreviewTitle">
              {{ activePreviewTitle }}
            </span>
            <span v-if="maxCount > 1" class="badge badge-primary flex-shrink-0">
              {{ previewActiveIndex + 1 }} / {{ maxCount }}
            </span>
          </div>
        </div>

        <!-- Body with Image & Navigation -->
        <div class="image-preview-body">
          <!-- Previous Button (if multi) -->
          <button
            v-if="previewTotalCount > 1"
            type="button"
            class="preview-nav-btn preview-nav-prev"
            :disabled="previewActiveIndex === 0"
            :title="$t('sendReport.prevPhoto') || text('รูปก่อนหน้า', 'Previous photo')"
            :aria-label="$t('sendReport.prevPhoto') || text('รูปก่อนหน้า', 'Previous photo')"
            @click.stop="prevPreview"
          >
            ❮
          </button>

          <!-- Main Image -->
          <div class="image-preview-wrapper" @click="closePreview">
            <img
              :src="activePreviewUrl"
              :alt="activePreviewTitle"
              class="preview-full-img"
              @click.stop
            />
          </div>

          <!-- Next Button (if multi) -->
          <button
            v-if="previewTotalCount > 1"
            type="button"
            class="preview-nav-btn preview-nav-next"
            :disabled="previewActiveIndex >= previewTotalCount - 1"
            :title="$t('sendReport.nextPhoto') || text('รูปถัดไป', 'Next photo')"
            :aria-label="$t('sendReport.nextPhoto') || text('รูปถัดไป', 'Next photo')"
            @click.stop="nextPreview"
          >
            ❯
          </button>
        </div>

        <!-- Ribbon / Timestamp / Info (if available) -->
        <div v-if="activePreviewTimestamp" class="image-preview-timestamp py-1 px-3 bg-light border-top">
          <small class="text-muted font-weight-bold">{{ activePreviewTimestamp }}</small>
        </div>

        <!-- Footer -->
        <div class="image-preview-footer">
          <div class="preview-footer-left text-muted small text-truncate mr-2">
            {{ activePreviewTitle }}
          </div>
          <button
            type="button"
            class="btn btn-sm btn-secondary font-weight-bold px-3 preview-footer-close-btn"
            @click="closePreview"
          >
            {{ $t('ticketDetail.close') || text('ปิด', 'Close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { compressImageToMax1MB, MAX_IMAGE_SIZE_BYTES, ACCEPT_ALL_IMAGES } from '@/utils/imageCompressor';
import { applyTechnicianWatermark, formatWatermarkDateTime, extractExifDate } from '@/utils/imageWatermark';
import { registerDialog } from '@/utils/dialogAccessibility';

export default {
  name: 'ImageAttachment',
  props: {
    preview: { type: String, default: '' },
    previews: { type: Array, default: () => [] },
    filename: { type: String, default: '' },
    accept: { type: String, default: ACCEPT_ALL_IMAGES },
    disabled: Boolean,
    invalid: Boolean,
    removable: { type: Boolean, default: true },
    watermarkOptions: { type: Object, default: null },
    maxCount: { type: Number, default: 1 },
    showTimestamp: { type: Boolean, default: null },
  },
  data: () => ({
    fileError: '',
    isCompressing: false,
    previewError: false,
    fileSizeBytes: null,
    lastCaptureSource: 'gallery',
    capturedTimestampDisplay: '',
    isLiveCapture: false,
    isOldFile: false,
    captureDateLabel: '',
    originalRawFile: null,
    capturedAtDate: null,
    windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
    // Multi items list: Array of { file, originalFile, capturedAt, url, name, displayTime, isLive }
    multiItems: [],
    showPreviewModal: false,
    previewActiveIndex: 0,
  }),
  mounted() {
    this.handleResize = () => {
      this.windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize);
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined' && this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.handlePreviewKeydown);
    }
    this.releaseDialog();
    // Clean up created object URLs
    this.multiItems.forEach(item => {
      if (item.url && item.url.startsWith('blob:')) {
        URL.revokeObjectURL(item.url);
      }
    });
  },
  watch: {
    preview(newVal) {
      this.previewError = false;
      if (!newVal) {
        this.fileSizeBytes = null;
        this.capturedTimestampDisplay = '';
        this.originalRawFile = null;
        if (this.maxCount <= 1) {
          this.closePreview();
        }
      }
    },
    previews: {
      immediate: true,
      handler(newVal) {
        if (this.maxCount > 1 && Array.isArray(newVal) && newVal.length === 0 && this.multiItems.length > 0) {
          // Sync reset
          this.multiItems = [];
          this.closePreview();
        }
      }
    },
    showPreviewModal(val) {
      if (val) {
        if (typeof window !== 'undefined') {
          window.addEventListener('keydown', this.handlePreviewKeydown);
        }
        this.$nextTick(this.activateDialog);
      } else {
        if (typeof window !== 'undefined') {
          window.removeEventListener('keydown', this.handlePreviewKeydown);
        }
        this.releaseDialog();
      }
    },
    async currentLang(newLang) {
      if (!this.watermarkOptions || this.isCompressing) return;
      if (this.maxCount > 1 && this.multiItems.length > 0) {
        for (let i = 0; i < this.multiItems.length; i++) {
          const item = this.multiItems[i];
          if (item.originalFile) {
            try {
              const processed = await applyTechnicianWatermark(item.originalFile, {
                ...this.watermarkOptions,
                lang: newLang,
                source: item.isLive ? 'camera' : 'gallery',
                isLive: item.isLive,
                capturedAt: item.capturedAt || new Date(),
              });
              const final = await compressImageToMax1MB(processed);
              item.file = final || processed;
              if (item.url && item.url.startsWith('blob:')) {
                URL.revokeObjectURL(item.url);
              }
              item.url = URL.createObjectURL(item.file);
            } catch (e) {
              // Graceful fallback
            }
          }
        }
        const allFiles = this.multiItems.map(item => item.file);
        this.$emit('change', {
          target: { files: allFiles, value: '' },
          files: allFiles,
          source: this.lastCaptureSource
        });
        this.$emit('files-selected', allFiles);
      } else if (this.originalRawFile) {
        try {
          const processed = await applyTechnicianWatermark(this.originalRawFile, {
            ...this.watermarkOptions,
            lang: newLang,
            source: this.isLiveCapture ? 'camera' : 'gallery',
            isLive: this.isLiveCapture,
            capturedAt: this.capturedAtDate || new Date(),
          });
          const final = await compressImageToMax1MB(processed);
          if (final) {
            this.$emit('change', {
              target: { files: [final], value: '', blur() {} },
              source: this.lastCaptureSource,
              photoTimestamp: final.watermarkInfo?.photoTimestamp || new Date().toISOString(),
              displayTime: this.capturedTimestampDisplay,
            });
            this.$emit('file-selected', final, {
              source: this.lastCaptureSource,
              photoTimestamp: final.watermarkInfo?.photoTimestamp || new Date().toISOString(),
              displayTime: this.capturedTimestampDisplay,
            });
          }
        } catch (e) {
          // Graceful fallback
        }
      }
    }
  },
  computed: {
    isMobileOrTablet() {
      if (typeof navigator === 'undefined') return false;
      const ua = navigator.userAgent;
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
      const isIPadSafari = /Macintosh/i.test(ua) && Boolean(navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
      
      // DevTools Device Mode emulation detection (e.g., F12 toggle device toolbar)
      const isDevToolsMobile = this.windowWidth <= 768 && (
        (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) ||
        (typeof window !== 'undefined' && 'ontouchstart' in window) ||
        this.windowWidth < 600
      );

      return isMobileUA || isIPadSafari || isDevToolsMobile;
    },
    loadingMessage() {
      if (this.watermarkOptions) {
        return this.text('กำลังประทับลายน้ำ Timestamp และปรับขนาดรูปภาพ...', 'Applying timestamp watermark and optimizing image...');
      }
      return this.$t('sendReport.optimizingImage') || this.text('กำลังปรับขนาดรูปภาพอัตโนมัติ (ไม่เกิน 1MB)...', 'Optimizing image (max 1MB)...');
    },
    fileExtension() {
      if (!this.filename) return '';
      return this.filename.split('.').pop().toLowerCase();
    },
    formattedSize() {
      if (!this.fileSizeBytes) return '';
      const kb = this.fileSizeBytes / 1024;
      if (kb < 1024) {
        return `${Math.round(kb)} KB`;
      }
      return `${(kb / 1024).toFixed(2)} MB`;
    },
    currentLang() {
      return (this.$store?.state?.locale?.lang || this.$i18n?.locale || 'th').toLowerCase();
    },
    isEn() {
      return this.currentLang.startsWith('en');
    },
    currentTimestampDisplay() {
      if (!this.capturedTimestampDisplay) return '';
      const raw = this.capturedTimestampDisplay;
      const timeOnly = raw.replace(/^(เวลาของไฟล์ในเครื่อง|เวลาถ่ายรูป|เวลาถ่ายสด|Device File Time|Photo Time):\s*/i, '');
      const label = this.isLiveCapture
        ? (this.isEn ? 'Photo Time: ' : 'เวลาถ่ายรูป: ')
        : (this.isEn ? 'Device File Time: ' : 'เวลาของไฟล์ในเครื่อง: ');
      return `${label}${timeOnly}`;
    },
    hasTimestamp() {
      if (typeof this.showTimestamp === 'boolean') {
        return this.showTimestamp;
      }
      return Boolean(this.watermarkOptions);
    },
    previewTotalCount() {
      if (this.maxCount > 1 && this.multiItems.length > 0) {
        return this.multiItems.length;
      }
      return this.preview ? 1 : 0;
    },
    activePreviewItem() {
      if (this.maxCount > 1 && this.multiItems.length > 0) {
        return this.multiItems[this.previewActiveIndex] || this.multiItems[0];
      }
      return null;
    },
    activePreviewUrl() {
      if (this.activePreviewItem) {
        return this.activePreviewItem.url;
      }
      return this.preview || '';
    },
    activePreviewTitle() {
      if (this.activePreviewItem) {
        return this.activePreviewItem.name || (this.text('รูปภาพที่ ', 'Photo ') + (this.previewActiveIndex + 1));
      }
      return this.filename || this.$t('sendReport.imageLabel') || this.text('รูปภาพที่แนบ', 'Attached photo');
    },
    activePreviewTimestamp() {
      if (this.activePreviewItem && this.hasTimestamp) {
        return this.getItemTimestampDisplay(this.activePreviewItem);
      }
      if (this.hasTimestamp && this.currentTimestampDisplay) {
        return this.currentTimestampDisplay;
      }
      return '';
    },
  },
  methods: {
    text(th, en) {
      return this.isEn ? en : th;
    },
    getItemTimestampDisplay(item) {
      if (!item) return '';
      const raw = item.displayTime || '';
      const timeOnly = raw.replace(/^(เวลาของไฟล์ในเครื่อง|เวลาถ่ายรูป|เวลาถ่ายสด|Device File Time|Photo Time):\s*/i, '');
      const label = item.isLive
        ? (this.isEn ? 'Photo Time: ' : 'เวลาถ่ายรูป: ')
        : (this.isEn ? 'Device File Time: ' : 'เวลาของไฟล์ในเครื่อง: ');
      return `${label}${timeOnly}`;
    },
    handleCameraClick() {
      if (this.disabled || this.isCompressing) return;
      if (this.$refs.camera) {
        this.$refs.camera.click();
      }
    },
    openPreview(idx = 0) {
      if (this.disabled || this.isCompressing) return;
      const total = this.previewTotalCount;
      if (total <= 0) return;
      this.previewActiveIndex = Math.max(0, Math.min(idx, total - 1));
      this.showPreviewModal = true;
      this.$emit('preview-image', {
        url: this.activePreviewUrl,
        name: this.activePreviewTitle,
        index: this.previewActiveIndex
      });
    },
    closePreview() {
      this.showPreviewModal = false;
      this.releaseDialog();
    },
    prevPreview() {
      if (this.previewActiveIndex > 0) {
        this.previewActiveIndex -= 1;
      }
    },
    nextPreview() {
      if (this.previewActiveIndex < this.previewTotalCount - 1) {
        this.previewActiveIndex += 1;
      }
    },
    handlePreviewKeydown(event) {
      if (!this.showPreviewModal) return;
      if (event.key === 'Escape' || event.keyCode === 27) {
        this.closePreview();
      } else if (event.key === 'ArrowLeft' || event.keyCode === 37) {
        this.prevPreview();
      } else if (event.key === 'ArrowRight' || event.keyCode === 39) {
        this.nextPreview();
      }
    },
    activateDialog() {
      if (!this.showPreviewModal || this._previewDialog || this._isBeingDestroyed || this._isDestroyed) return;
      if (!this.$refs.previewDialog) return;
      try {
        this._previewDialog = registerDialog(this.$refs.previewDialog, {
          layers: () => [this.$refs.previewDialog],
          initialFocus: () => this.$refs.previewDialog.querySelector('.preview-footer-close-btn') || this.$refs.previewDialog,
          close: this.closePreview,
        });
      } catch (e) {
        // Safe fallback in test or SSR environments
      }
    },
    releaseDialog() {
      if (this._previewDialog) {
        try {
          this._previewDialog.release();
        } catch (e) {}
        this._previewDialog = null;
      }
    },
    async emitFile(file, source = 'gallery') {
      if (this.disabled || !file) return;
      this.fileError = '';
      this.isCompressing = true;
      this.lastCaptureSource = source;
      const isLive = source === 'camera' || Boolean(file.lastModified && (Date.now() - file.lastModified < 180000));
      this.isLiveCapture = isLive;

      try {
        let processedFile = file;
        let isOldFile = false;
        let dateLabel = '';

        const capturedDate = isLive ? new Date() : (file.lastModified ? new Date(file.lastModified) : new Date());
        this.originalRawFile = file;
        this.capturedAtDate = capturedDate;

        // Apply Canvas Watermark if watermarkOptions are provided
        if (this.watermarkOptions) {
          processedFile = await applyTechnicianWatermark(file, {
            ...this.watermarkOptions,
            lang: this.currentLang,
            source,
            isLive,
            capturedAt: capturedDate,
          });
          if (processedFile.watermarkInfo) {
            this.capturedTimestampDisplay = processedFile.watermarkInfo.displayTime || formatWatermarkDateTime(new Date(), true);
            this.isLiveCapture = processedFile.watermarkInfo.isLiveCamera;
            this.isOldFile = false;
            this.captureDateLabel = processedFile.watermarkInfo.label;
            isOldFile = false;
            dateLabel = processedFile.watermarkInfo.label;
          }
        } else {
          // Non-watermarked forms (e.g. user send report)
          this.isLiveCapture = isLive;
          this.isOldFile = false;
          if (this.hasTimestamp) {
            this.capturedTimestampDisplay = formatWatermarkDateTime(capturedDate, true);
          } else {
            this.capturedTimestampDisplay = '';
          }

          processedFile = await compressImageToMax1MB(file);
        }

        const finalFile = processedFile || file;
        if (finalFile.size > MAX_IMAGE_SIZE_BYTES) throw new Error(this.text('ไม่สามารถย่อรูปให้ไม่เกิน 1 MB ได้ กรุณาเลือกรูปอื่น', 'Could not reduce this image to 1 MB. Please choose another image.'));
        this.fileSizeBytes = finalFile.size || file.size;

        if (this.maxCount > 1) {
          // Multi-mode: append to multiItems (up to maxCount)
          if (this.multiItems.length < this.maxCount) {
            let blobUrl = '';
            try {
              blobUrl = URL.createObjectURL(finalFile);
            } catch (e) {
              blobUrl = '';
            }
            this.multiItems.push({
              file: finalFile,
              originalFile: file,
              capturedAt: capturedDate,
              url: blobUrl,
              name: finalFile.name || `photo_${this.multiItems.length + 1}.jpg`,
              displayTime: this.capturedTimestampDisplay,
              isLive: this.isLiveCapture,
              isOldFile: false,
              dateLabel,
            });

            const allFiles = this.multiItems.map(item => item.file);
            this.$emit('change', {
              target: { files: allFiles, value: '' },
              files: allFiles,
              source
            });
            this.$emit('files-selected', allFiles);
            this.$emit('file-selected', finalFile);
          }
        } else {
          // Single-mode
          this.$emit('change', {
            target: { files: [finalFile], value: '', blur() {} },
            source,
            photoTimestamp: finalFile.watermarkInfo?.photoTimestamp || new Date().toISOString(),
            displayTime: this.capturedTimestampDisplay,
            isOldFile,
            dateLabel
          });
          this.$emit('file-selected', finalFile, {
            source,
            photoTimestamp: finalFile.watermarkInfo?.photoTimestamp || new Date().toISOString(),
            displayTime: this.capturedTimestampDisplay,
            isOldFile,
            dateLabel
          });
        }
      } catch (err) {
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
          this.fileError = this.text('ไม่สามารถย่อรูปให้ไม่เกิน 1 MB ได้ กรุณาเลือกรูปอื่น', 'Could not reduce this image to 1 MB. Please choose another image.');
          return;
        }
        console.error('Image processing fallback:', err);
        this.fileSizeBytes = file.size;
        this.capturedTimestampDisplay = formatWatermarkDateTime(file.lastModified ? new Date(file.lastModified) : new Date(), true);
        this.$emit('change', {
          target: { files: [file], value: '', blur() {} },
          source
        });
        this.$emit('file-selected', file, { source });
      } finally {
        this.isCompressing = false;
      }
    },
    removeMultiItem(idx) {
      if (this.disabled) return;
      const removed = this.multiItems.splice(idx, 1);
      if (removed[0] && removed[0].url && removed[0].url.startsWith('blob:')) {
        URL.revokeObjectURL(removed[0].url);
      }
      if (this.multiItems.length === 0) {
        this.closePreview();
      } else if (this.previewActiveIndex >= this.multiItems.length) {
        this.previewActiveIndex = this.multiItems.length - 1;
      }
      const allFiles = this.multiItems.map(item => item.file);
      this.$emit('change', {
        target: { files: allFiles, value: '' },
        files: allFiles
      });
      this.$emit('files-selected', allFiles);
      this.$emit('remove', idx);
    },
    async selectImage(event, source = 'gallery') {
      const files = event.target.files ? Array.from(event.target.files) : [];
      if (files.length === 0) return;

      if (this.maxCount > 1) {
        const availableSlots = this.maxCount - this.multiItems.length;
        const toProcess = files.slice(0, availableSlots);
        for (const file of toProcess) {
          await this.emitFile(file, source);
        }
      } else {
        await this.emitFile(files[0], source);
      }
      event.target.value = '';
      event.target.blur();
    },
    dropImage(event) {
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) {
        this.emitFile(file, 'gallery');
      }
    },
    handleRemove() {
      this.closePreview();
      this.capturedTimestampDisplay = '';
      this.$emit('remove');
    }
  },
};
</script>

<style scoped>
.image-attachment {
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.attachment-choices {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  flex: 1;
  height: 100%;
}

/* Multi-file container & grid */
.attachment-multi-container {
  width: 100%;
}

.multi-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
}

.multi-item-card {
  min-height: 180px;
}

.multi-item-card .attachment-preview img {
  height: 120px;
  object-fit: contain;
}

.attachment-add-more {
  width: 100%;
  margin-top: 8px;
}

.btn-add-more {
  width: 100%;
  border: 1.5px dashed #2563eb;
  background: #f8fafc;
  color: #1e40af;
  border-radius: 8px;
  padding: 8px 14px;
  min-height: 40px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.btn-add-more:hover:not(:disabled) {
  background: #eff6ff;
  border-color: #1d4ed8;
  border-style: solid;
  transform: translateY(-1px);
}

/* Dual choice buttons */
.attachment-pick {
  flex: 1 1 140px;
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 14px;
  min-height: 110px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

/* Take Photo Button (Mobile/Tablet only): Blue Border */
.btn-take-photo {
  border: 2px solid #1e40af;
  color: #1e40af;
  background: #f8fafc;
}
.btn-take-photo:hover:not(:disabled) {
  background: #eff6ff;
  border-color: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.12);
}

/* Choose Photo Button: Blue Border matching Take Photo */
.btn-choose-photo {
  border: 2px solid #1e40af;
  color: #1e40af;
  background: #ffffff;
}
.btn-choose-photo:hover:not(:disabled) {
  background: #eff6ff;
  border-color: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.12);
}

.attachment-card {
  background: #ffffff;
  border: 2px solid #1e40af;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(37, 99, 235, 0.08);
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
}

.attachment-loading-card {
  background: #f8fafc;
  border: 2px dashed #1e40af;
  border-radius: 12px;
  min-height: 110px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
}

.attachment-preview {
  background: #f8f9fa;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.attachment-preview img {
  width: 100%;
  max-height: 240px;
  object-fit: contain;
  transition: transform 0.25s ease-in-out;
}

/* Interactive preview click styles */
.clickable-preview {
  cursor: pointer;
  user-select: none;
}

.clickable-preview:hover img,
.clickable-preview:focus-visible img {
  transform: scale(1.05);
}

.clickable-preview:focus-visible {
  outline: 3px solid #1e40af;
  outline-offset: -3px;
}

.preview-badge {
  position: absolute;
  bottom: 6px;
  right: 6px;
  background: rgba(15, 23, 42, 0.75);
  color: #ffffff;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
  pointer-events: none;
  transition: opacity 0.2s ease;
  z-index: 2;
}

.preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  color: #ffffff;
  pointer-events: none;
  z-index: 3;
}

.clickable-preview:hover .preview-overlay,
.clickable-preview:focus-visible .preview-overlay {
  opacity: 1;
}

.clickable-preview:hover .preview-badge {
  opacity: 0;
}

.preview-overlay-icon {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
}

.preview-overlay-text {
  font-size: 11px;
  font-weight: 600;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
  letter-spacing: 0.2px;
}

.clickable-name:hover {
  color: #1e40af !important;
  text-decoration: underline;
}

.attachment-timestamp-ribbon {
  background: #f1f5f9;
  border-top: 1px solid #e2e8f0;
  padding: 6px 14px;
}

.attachment-footer {
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  border-top: 1px solid #eee;
}

.attachment-name {
  flex: 1 1 80px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: #334155;
}

.attachment-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* Footer Action Buttons with explicit colors */
.btn-change-image {
  border: 1px solid #1e40af !important;
  color: #1e40af !important;
  background: transparent;
  font-weight: 500;
}
.btn-change-image:hover:not(:disabled) {
  background: #eff6ff;
  color: #1d4ed8 !important;
}

.btn-choose-new {
  border: 1px solid #1e40af !important;
  color: #1e40af !important;
  background: transparent;
  font-weight: 500;
}
.btn-choose-new:hover:not(:disabled) {
  background: #eff6ff;
  color: #1d4ed8 !important;
}

.btn-delete-image {
  border: 1px solid #dc2626 !important;
  color: #dc2626 !important;
  background: transparent;
  font-weight: 500;
}
.btn-delete-image:hover:not(:disabled) {
  background: #fef2f2;
  color: #b91c1c !important;
}

button:focus-visible {
  outline: 3px solid #93c5fd;
  outline-offset: 3px;
}
button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Modal / Lightbox Styles */
.image-preview-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 3000;
  background-color: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}

.image-preview-dialog {
  background: #ffffff;
  border-radius: 14px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  animation: previewPopIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes previewPopIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.image-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
}

.preview-title {
  font-size: 0.95rem;
  color: #1e293b;
  max-width: 400px;
}

.preview-close-btn {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
  line-height: 1;
}

.preview-close-btn:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.image-preview-body {
  position: relative;
  background: #0f172a;
  min-height: 250px;
  max-height: calc(85vh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 12px;
}

.image-preview-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.preview-full-img {
  max-width: 100%;
  max-height: calc(85vh - 140px);
  object-fit: contain;
  border-radius: 4px;
  cursor: default;
}

.preview-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.1rem;
  transition: all 0.2s ease;
  z-index: 5;
}

.preview-nav-prev {
  left: 14px;
}

.preview-nav-next {
  right: 14px;
}

.preview-nav-btn:hover:not(:disabled) {
  background: rgba(30, 64, 175, 0.9);
  border-color: #3b82f6;
  transform: translateY(-50%) scale(1.08);
}

.preview-nav-btn:disabled {
  opacity: 0.25;
  cursor: not-allowed;
}

.image-preview-timestamp {
  background: #f8fafc;
}

.image-preview-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 18px;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
}
</style>
